<!-- Hero Section with Background Image -->
<div class="clinics-hero">
  <div class="clinics-hero-bg"></div>
  <div class="clinics-hero-overlay"></div>
  <div class="container-xxl py-10 position-relative">
    <!-- Page Header -->
    <div class="d-flex flex-wrap flex-stack mb-10">
      <h1 class="fw-bold my-2 text-white"><i class="fa fa-heart me-3" style="color: #e74c3c;"></i>{{ 'trainings.title' | translate }}</h1>
    </div>

    <!-- Filters Section -->
    <div class="card mb-0 shadow-lg">
      <div class="card-body">
        <div class="row g-4 align-items-end">
          <!-- Search Filter -->
          <div class="col-xl-2 col-lg-4 col-md-6">
            <app-input-component
              [type]="'text'"
              [(value)]="searchQuery"
              [label]="'trainings.search' | translate"
              [placeholder]="'trainings.searchPlaceholder' | translate"
              [floating]="true"
              (valueChange)="onSearchChange()"
            />
          </div>

          <!-- Discipline Filter -->
          <div class="col-xl-2 col-lg-4 col-md-6">
            <app-selectorini
              [options]="disciplineOptions"
              [displayKey]="'label'"
              [valueKey]="'value'"
              [(value)]="selectedDiscipline"
              [label]="'trainings.discipline' | translate"
              [placeholder]="'trainings.allDisciplines' | translate"
              [floating]="true"
              (valueChange)="onDisciplineChange()"
            />
          </div>

          <!-- Category Filter -->
          <div class="col-xl-2 col-lg-4 col-md-6">
            <app-selectorini
              [options]="categoryOptions"
              [displayKey]="'name'"
              [valueKey]="'id'"
              [(value)]="selectedCategory"
              [label]="'trainings.category' | translate"
              [placeholder]="'trainings.allCategories' | translate"
              [floating]="true"
              (valueChange)="onCategoryChange()"
            />
          </div>

          <!-- Company Filter -->
          <div class="col-xl-2 col-lg-4 col-md-6">
            <app-selectorini
              [options]="companies"
              [displayKey]="'company_name'"
              [valueKey]="'company_id'"
              [(value)]="selectedCompanyId"
              [label]="'competitions.organizer' | translate"
              [placeholder]="'competitions.allOrganizers' | translate"
              [floating]="true"
              (valueChange)="onCompanyChange()"
            />
          </div>

          <!-- Date Range Filter -->
          <div class="col-xl-2 col-lg-4 col-md-6">
            <app-date-range-picker
              [(fromDate)]="dateFrom"
              [(toDate)]="dateTo"
              [label]="'competitions.dateRange' | translate"
              [placeholder]="'competitions.selectDates' | translate"
              (rangeChange)="onDateFilterChange()"
            />
          </div>

          <!-- Buttons -->
          <div class="col-xl-2 col-lg-4 col-md-6">
            <div class="d-flex gap-4">
              <!-- Sort by Nearest Button -->
              <button
                class="btn flex-grow-1"
                [class.btn-primary]="sortByDistance"
                [class.btn-light]="!sortByDistance"
                (click)="toggleSortByDistance()"
                [disabled]="!userLocation"
                style="height: 50px;"
                [title]="userLocation ? ('competitions.sortByNearest' | translate) : ('competitions.waitingForLocation' | translate)"
              >
                @if (locationLoading) {
                  <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                } @else {
                  <i class="fa fa-location-crosshairs me-2"></i>
                }
                {{ 'competitions.nearest' | translate }}
              </button>

              <!-- Clear Filters Button -->
              <app-button
                [variant]="'neutral'"
                [iconClass]="'fa fa-times'"
                (clicked)="clearFilters()"
                [title]="'competitions.clearFilters' | translate"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="container-xxl py-10">

  <!-- Map Section -->
  @if (!loading && filteredEvents.length > 0) {
    <div class="mb-10">
      <app-location-map
        [markers]="mapMarkers"
        [config]="mapConfig"
        [loading]="geocodingInProgress"
        (markerClick)="onMapMarkerClick($event)"
        (infoWindowAction)="onMapInfoWindowAction($event)"
      />
    </div>
  }

  <!-- Loading State -->
  @if (loading) {
    <div class="text-center py-20">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'trainings.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'trainings.loading' | translate }}</p>
    </div>
  }

  <!-- No Results -->
  @if (!loading && filteredEvents.length === 0) {
    <div class="card">
      <div class="card-body text-center py-20">
        <i class="fa fa-chalkboard-user fs-3x text-muted mb-5"></i>
        <h3 class="text-muted">{{ 'trainings.noResults' | translate }}</h3>
        <p class="text-muted">{{ 'trainings.noResultsHint' | translate }}</p>
      </div>
    </div>
  }

  <!-- Upcoming Events -->
  @if (!loading && upcomingEvents.length > 0) {
    <div class="mb-10">
      <div class="d-flex flex-wrap flex-stack mb-6">
        <h2 class="fw-bold my-2">
          <i class="fa fa-chalkboard-user text-primary me-3"></i>{{ 'trainings.upcoming' | translate }}
        </h2>
        <app-button
          [variant]="'primary'"
          [label]="'common.createEvent' | translate"
          [iconClass]="'fa fa-plus'"
          (clicked)="openCreateEvent()"
        />
      </div>
      <div class="row g-6 g-xl-9">
        @for (event of upcomingEvents; track event.event_id) {
          <div class="col-md-6 col-lg-4">
            <div class="card h-100 event-card" [id]="'event-card-' + event.event_id">
            <!-- Event Image -->
            <div class="event-image-container position-relative">
              @if (event.header_url) {
                <img [src]="event.header_url" class="card-img-top event-image" [alt]="event.event_name"/>
              } @else {
                <div class="event-image-placeholder bg-light d-flex align-items-center justify-content-center">
                  <i class="fa fa-chalkboard-user fs-3x text-muted"></i>
                </div>
              }

              <!-- Event Status Badge -->
              <div class="position-absolute top-0 end-0 m-3">
                @if (isEventUpcoming(event.start_date)) {
                  <span class="badge badge-success badge-lg">{{ 'event.badge.upcoming' | translate }}</span>
                } @else if (isEventPast(event.start_date)) {
                  <span class="badge badge-secondary badge-lg">{{ 'event.badge.past' | translate }}</span>
                }
              </div>

              <!-- Distance Badge -->
              @if (userLocation && getEventDistance(event) !== null) {
                <div class="position-absolute top-0 start-0 m-3">
                  <span class="badge badge-primary badge-lg">
                    <i class="fa fa-location-arrow me-1"></i>
                    {{ formatDistance(getEventDistance(event)) }}
                  </span>
                </div>
              }
            </div>

            <div class="card-body d-flex flex-column">
              <!-- Event Category Badge -->
              <div class="mb-3">
                <span class="badge" [ngClass]="getCategoryBadgeClass(event)">{{ getCategoryDisplay(event) }}</span>
              </div>

              <!-- Event Name -->
              <h3 class="card-title fw-bold fs-4 mb-2">{{ event.event_name }}</h3>

              <!-- Organiser -->
              @if (event.company) {
                <div class="mb-3">
                  @if (event.company.is_personal) {
                    <span class="text-muted fs-7">
                      <i class="fa fa-user me-1"></i>{{ event.company.company_name }}
                    </span>
                  } @else {
                    <a class="text-muted fs-7 text-decoration-none organiser-link" [routerLink]="['/organisations', event.company.company_id]">
                      <i class="fa fa-building me-1"></i>{{ event.company.company_name }}
                    </a>
                  }
                </div>
              }

              <!-- Event Details -->
              <div class="mb-5 mt-auto">
                <div class="row g-3">
                  <!-- Left Column -->
                  <div class="col-6">
                    <!-- Dates -->
                    @if (event.start_date) {
                      <div class="d-flex align-items-start mb-3">
                        <i class="fa fa-calendar text-primary me-2 fs-6 mt-1"></i>
                        <div>
                          <div class="fw-semibold fs-7">{{ formatDateDMYDots(event.start_date) }}</div>
                          @if (event.end_date && event.start_date !== event.end_date) {
                            <div class="text-muted fs-8">{{ 'event.dateTo' | translate }} {{ formatDateDMYDots(event.end_date) }}</div>
                          }
                        </div>
                      </div>
                    }

                    <!-- Location -->
                    <div class="d-flex align-items-start mb-3">
                      <i class="fa fa-location-dot text-primary me-2 fs-6 mt-1"></i>
                      <div>
                        <div class="fw-semibold fs-7">{{ event.city }}, {{ event.country }}</div>
                        @if (event.address) {
                          <a [href]="getGoogleMapsUrl(event)" target="_blank" class="text-muted fs-8 address-link text-decoration-none">
                            {{ event.address }}
                            <i class="fa fa-external-link ms-1 fs-9"></i>
                          </a>
                        }
                      </div>
                    </div>

                    <!-- Registration Deadline -->
                    @if (event.registration_deadline) {
                      <div class="d-flex align-items-start">
                        <i class="fa fa-clock text-warning me-2 fs-6 mt-1"></i>
                        <div>
                          <div class="text-muted fs-8">{{ 'event.regDeadline' | translate }}</div>
                          <div class="fw-semibold fs-7">{{ formatDateDMYDots(event.registration_deadline) }}</div>
                        </div>
                      </div>
                    }
                  </div>

                  <!-- Right Column -->
                  <div class="col-6">
                    <!-- Available Packages (Training events only) -->
                    @if (isTrainingEvent(event)) {
                      <div class="d-flex align-items-start mb-3 hover-info-container" (mouseenter)="loadEventPackages(event.event_id)">
                        <i class="fa fa-box-open text-info me-2 fs-6 mt-1"></i>
                        <div class="flex-grow-1">
                          <div class="fw-semibold fs-7">{{ 'event.packages' | translate }}</div>
                          <div class="text-muted fs-8">{{ 'event.hoverToView' | translate }}</div>
                        </div>
                        <div class="hover-popup">
                          <div class="hover-popup-content">
                            <div class="hover-popup-header">
                              <i class="fa fa-box-open me-2"></i>{{ 'event.availablePackages' | translate }}
                            </div>
                            <div class="hover-popup-body">
                              @if (loadingPackages[event.event_id]) {
                                <div class="text-center py-2">
                                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                  </div>
                                </div>
                              } @else if (eventPackages[event.event_id] && eventPackages[event.event_id].length > 0) {
                                <ul>
                                  @for (pkg of eventPackages[event.event_id]; track pkg.package_id) {
                                    <li>
                                      <i class="fa fa-circle-check"></i>
                                      <strong>{{ pkg.package_name }}</strong>
                                      <span class="ms-2 package-availability-badge" [ngClass]="getPackageAvailabilityBadge(pkg).class">
                                        {{ getPackageAvailabilityBadge(pkg).text }}
                                      </span>
                                      @if (pkg.price_cents > 0) {
                                        <span class="text-muted ms-2">({{ formatPackagePrice(pkg.price_cents, pkg.currency) }})</span>
                                      }
                                    </li>
                                  }
                                </ul>
                              } @else {
                                <div class="text-muted text-center py-2">{{ 'event.noPackages' | translate }}</div>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Available Services -->
                    <div class="d-flex align-items-start hover-info-container" (mouseenter)="loadEventServices(event.event_id)">
                      <i class="fa fa-concierge-bell text-success me-2 fs-6 mt-1"></i>
                      <div class="flex-grow-1">
                        <div class="fw-semibold fs-7">{{ 'event.services' | translate }}</div>
                        <div class="text-muted fs-8">{{ 'event.hoverToView' | translate }}</div>
                      </div>
                      <div class="hover-popup">
                        <div class="hover-popup-content">
                          <div class="hover-popup-header">
                            <i class="fa fa-concierge-bell me-2"></i>{{ 'event.availableServices' | translate }}
                          </div>
                          <div class="hover-popup-body">
                            @if (loadingServices[event.event_id]) {
                              <div class="text-center py-2">
                                <div class="spinner-border spinner-border-sm text-primary" role="status">
                                  <span class="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            } @else if (eventServices[event.event_id] && eventServices[event.event_id].length > 0) {
                              <ul>
                                @for (service of eventServices[event.event_id]; track service.event_service_id) {
                                  <li>
                                    <i class="fa fa-circle-check"></i>
                                    <strong>{{ service.event_service_name }}</strong>
                                    @if (service.max_num_registrations) {
                                      <span class="service-slots-badge">
                                        {{ getServiceAvailableSlots(service) }}/{{ service.max_num_registrations }} {{ 'event.slots' | translate }}
                                      </span>
                                    }
                                    @if (service.price) {
                                      <span class="text-muted ms-2">({{ service.price }} {{ service.price_currency || 'kr' }})</span>
                                    }
                                  </li>
                                }
                              </ul>
                            } @else {
                              <div class="text-muted text-center py-2">{{ 'event.noServices' | translate }}</div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Button -->
              <div class="mt-auto text-center">
                <app-button
                  [variant]="'primary'"
                  [label]="'competitions.viewDetails' | translate"
                  [routerLink]="'/event-details/' + event.event_id"
                />
              </div>
            </div>
          </div>
        </div>
        }
      </div>
    </div>
  }

  <!-- Divider -->
  @if (!loading && upcomingEvents.length > 0 && pastEvents.length > 0) {
    <div class="separator-section my-10">
      <div class="d-flex align-items-center">
        <div class="flex-grow-1 border-bottom border-gray-300"></div>
        <div class="px-5">
          <span class="badge badge-light-secondary badge-lg">{{ 'trainings.pastEvents' | translate }}</span>
        </div>
        <div class="flex-grow-1 border-bottom border-gray-300"></div>
      </div>
    </div>
  }

  <!-- Past Events -->
  @if (!loading && pastEvents.length > 0) {
    <div class="mb-10">
      @if (upcomingEvents.length === 0) {
        <h2 class="fw-bold mb-6">
          <i class="fa fa-chalkboard-user text-secondary me-3"></i>{{ 'trainings.past' | translate }}
        </h2>
      }
      <div class="row g-6 g-xl-9">
        @for (event of pastEvents; track event.event_id) {
          <div class="col-md-6 col-lg-4">
            <div class="card h-100 event-card" [id]="'event-card-' + event.event_id">
            <!-- Event Image -->
            <div class="event-image-container position-relative">
              @if (event.header_url) {
                <img [src]="event.header_url" class="card-img-top event-image" [alt]="event.event_name"/>
              } @else {
                <div class="event-image-placeholder bg-light d-flex align-items-center justify-content-center">
                  <i class="fa fa-chalkboard-user fs-3x text-muted"></i>
                </div>
              }

              <!-- Event Status Badge -->
              <div class="position-absolute top-0 end-0 m-3">
                @if (isEventUpcoming(event.start_date)) {
                  <span class="badge badge-success badge-lg">{{ 'event.badge.upcoming' | translate }}</span>
                } @else if (isEventPast(event.start_date)) {
                  <span class="badge badge-secondary badge-lg">{{ 'event.badge.past' | translate }}</span>
                }
              </div>

              <!-- Distance Badge -->
              @if (userLocation && getEventDistance(event) !== null) {
                <div class="position-absolute top-0 start-0 m-3">
                  <span class="badge badge-primary badge-lg">
                    <i class="fa fa-location-arrow me-1"></i>
                    {{ formatDistance(getEventDistance(event)) }}
                  </span>
                </div>
              }
            </div>

            <div class="card-body d-flex flex-column">
              <!-- Event Category Badge -->
              <div class="mb-3">
                <span class="badge" [ngClass]="getCategoryBadgeClass(event)">{{ getCategoryDisplay(event) }}</span>
              </div>

              <!-- Event Name -->
              <h3 class="card-title fw-bold fs-4 mb-2">{{ event.event_name }}</h3>

              <!-- Organiser -->
              @if (event.company) {
                <div class="mb-3">
                  @if (event.company.is_personal) {
                    <span class="text-muted fs-7">
                      <i class="fa fa-user me-1"></i>{{ event.company.company_name }}
                    </span>
                  } @else {
                    <a class="text-muted fs-7 text-decoration-none organiser-link" [routerLink]="['/organisations', event.company.company_id]">
                      <i class="fa fa-building me-1"></i>{{ event.company.company_name }}
                    </a>
                  }
                </div>
              }

              <!-- Event Details -->
              <div class="mb-5 mt-auto">
                <div class="row g-3">
                  <!-- Left Column -->
                  <div class="col-6">
                    <!-- Dates -->
                    @if (event.start_date) {
                      <div class="d-flex align-items-start mb-3">
                        <i class="fa fa-calendar text-primary me-2 fs-6 mt-1"></i>
                        <div>
                          <div class="fw-semibold fs-7">{{ formatDateDMYDots(event.start_date) }}</div>
                          @if (event.end_date && event.start_date !== event.end_date) {
                            <div class="text-muted fs-8">{{ 'event.dateTo' | translate }} {{ formatDateDMYDots(event.end_date) }}</div>
                          }
                        </div>
                      </div>
                    }

                    <!-- Location -->
                    <div class="d-flex align-items-start mb-3">
                      <i class="fa fa-location-dot text-primary me-2 fs-6 mt-1"></i>
                      <div>
                        <div class="fw-semibold fs-7">{{ event.city }}, {{ event.country }}</div>
                        @if (event.address) {
                          <a [href]="getGoogleMapsUrl(event)" target="_blank" class="text-muted fs-8 address-link text-decoration-none">
                            {{ event.address }}
                            <i class="fa fa-external-link ms-1 fs-9"></i>
                          </a>
                        }
                      </div>
                    </div>

                    <!-- Registration Deadline -->
                    @if (event.registration_deadline) {
                      <div class="d-flex align-items-start">
                        <i class="fa fa-clock text-warning me-2 fs-6 mt-1"></i>
                        <div>
                          <div class="text-muted fs-8">{{ 'event.regDeadline' | translate }}</div>
                          <div class="fw-semibold fs-7">{{ formatDateDMYDots(event.registration_deadline) }}</div>
                        </div>
                      </div>
                    }
                  </div>

                  <!-- Right Column -->
                  <div class="col-6">
                    <!-- Available Packages (Training events only) -->
                    @if (isTrainingEvent(event)) {
                      <div class="d-flex align-items-start mb-3 hover-info-container" (mouseenter)="loadEventPackages(event.event_id)">
                        <i class="fa fa-box-open text-info me-2 fs-6 mt-1"></i>
                        <div class="flex-grow-1">
                          <div class="fw-semibold fs-7">{{ 'event.packages' | translate }}</div>
                          <div class="text-muted fs-8">{{ 'event.hoverToView' | translate }}</div>
                        </div>
                        <div class="hover-popup">
                          <div class="hover-popup-content">
                            <div class="hover-popup-header">
                              <i class="fa fa-box-open me-2"></i>{{ 'event.availablePackages' | translate }}
                            </div>
                            <div class="hover-popup-body">
                              @if (loadingPackages[event.event_id]) {
                                <div class="text-center py-2">
                                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                  </div>
                                </div>
                              } @else if (eventPackages[event.event_id] && eventPackages[event.event_id].length > 0) {
                                <ul>
                                  @for (pkg of eventPackages[event.event_id]; track pkg.package_id) {
                                    <li>
                                      <i class="fa fa-circle-check"></i>
                                      <strong>{{ pkg.package_name }}</strong>
                                      <span class="ms-2 package-availability-badge" [ngClass]="getPackageAvailabilityBadge(pkg).class">
                                        {{ getPackageAvailabilityBadge(pkg).text }}
                                      </span>
                                      @if (pkg.price_cents > 0) {
                                        <span class="text-muted ms-2">({{ formatPackagePrice(pkg.price_cents, pkg.currency) }})</span>
                                      }
                                    </li>
                                  }
                                </ul>
                              } @else {
                                <div class="text-muted text-center py-2">{{ 'event.noPackages' | translate }}</div>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Available Services -->
                    <div class="d-flex align-items-start hover-info-container" (mouseenter)="loadEventServices(event.event_id)">
                      <i class="fa fa-concierge-bell text-success me-2 fs-6 mt-1"></i>
                      <div class="flex-grow-1">
                        <div class="fw-semibold fs-7">{{ 'event.services' | translate }}</div>
                        <div class="text-muted fs-8">{{ 'event.hoverToView' | translate }}</div>
                      </div>
                      <div class="hover-popup">
                        <div class="hover-popup-content">
                          <div class="hover-popup-header">
                            <i class="fa fa-concierge-bell me-2"></i>{{ 'event.availableServices' | translate }}
                          </div>
                          <div class="hover-popup-body">
                            @if (loadingServices[event.event_id]) {
                              <div class="text-center py-2">
                                <div class="spinner-border spinner-border-sm text-primary" role="status">
                                  <span class="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            } @else if (eventServices[event.event_id] && eventServices[event.event_id].length > 0) {
                              <ul>
                                @for (service of eventServices[event.event_id]; track service.event_service_id) {
                                  <li>
                                    <i class="fa fa-circle-check"></i>
                                    <strong>{{ service.event_service_name }}</strong>
                                    @if (service.max_num_registrations) {
                                      <span class="service-slots-badge">
                                        {{ getServiceAvailableSlots(service) }}/{{ service.max_num_registrations }} {{ 'event.slots' | translate }}
                                      </span>
                                    }
                                    @if (service.price) {
                                      <span class="text-muted ms-2">({{ service.price }} {{ service.price_currency || 'kr' }})</span>
                                    }
                                  </li>
                                }
                              </ul>
                            } @else {
                              <div class="text-muted text-center py-2">{{ 'event.noServices' | translate }}</div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Button -->
              <div class="mt-auto text-center">
                <app-button
                  [variant]="'primary'"
                  [label]="'competitions.viewDetails' | translate"
                  [routerLink]="'/event-details/' + event.event_id"
                />
              </div>
            </div>
          </div>
        </div>
        }
      </div>
    </div>
  }
</div>
